'use client';

import { useAuiState } from '@assistant-ui/react';
import { type JSX } from 'react';

import { useFeedbackContext } from '../../../_context/FeedbackContext';
import { FeedbackCommentBox } from './FeedbackCommentBox';
import { FeedbackConfirmation } from './FeedbackConfirmation';
import { FeedbackPrompt } from './FeedbackPrompt';

function useUserMessageCount(): number {
    return useAuiState(({ thread }) => thread.messages.filter((m) => m.role === 'user').length);
}

// `@assistant-ui/core` assigns synthetic ids when no real id has been persisted
// upstream: `__error__<7chars>` for failed stream turns and `__optimistic__<7chars>`
// for in-flight ones. Posting feedback against either would FK-fail in
// conv-search-api, so treat them as "no assistant message yet".
function isPersistedAssistantId(id: string): boolean {
    return !id.startsWith('__error__') && !id.startsWith('__optimistic__');
}

function useLatestAssistantMessageId(): string | null {
    return useAuiState(
        ({ thread }) =>
            thread.messages.findLast((m) => m.role === 'assistant' && isPersistedAssistantId(m.id))?.id ?? null,
    );
}

function useIsThreadRunning(): boolean {
    return useAuiState(({ thread }) => thread.isRunning);
}

export function FeedbackWidget(): JSX.Element | null {
    const { state, error, isPending, userMessageThreshold, recordRating, submitComment, dismiss } =
        useFeedbackContext();
    const userCount = useUserMessageCount();
    const latestAssistantId = useLatestAssistantMessageId();
    const isThreadRunning = useIsThreadRunning();

    if (state === 'dismissed') return null;
    // Stay hidden while in the prompt state until: threshold met, an assistant
    // message exists, and the assistant has finished streaming. Asking "did the
    // results meet your expectations?" while the response is still loading is
    // confusing — there's no result to rate yet.
    if (state === 'prompt' && (userCount < userMessageThreshold || !latestAssistantId || isThreadRunning)) {
        return null;
    }

    const onThumbUp = (): void => {
        if (latestAssistantId) void recordRating('up', latestAssistantId);
    };
    const onThumbDown = (): void => {
        if (state === 'comment') return;
        if (latestAssistantId) void recordRating('down', latestAssistantId);
    };
    const onSubmit = (comment: string): void => {
        if (latestAssistantId) void submitComment(comment, latestAssistantId);
    };

    return (
        <div className="mb-2 flex max-w-[90%] flex-col gap-2 rounded-md bg-gray-100 px-4 py-2">
            {state === 'confirmation' ? (
                <FeedbackConfirmation onDismiss={dismiss} />
            ) : (
                <>
                    <FeedbackPrompt
                        onThumbUp={onThumbUp}
                        onThumbDown={onThumbDown}
                        disabled={isPending}
                        selected={state === 'comment' ? 'down' : undefined}
                    />
                    {state === 'comment' && <FeedbackCommentBox onSubmit={onSubmit} disabled={isPending} />}
                    {error && (
                        <p className="text-center text-xs text-red-600" role="alert">
                            {error}
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
